import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import Header from "../component/header";
import Footer from "../component/Footer";
import {
  ArrowLeft,
  CalendarDays,
  ChevronRight,
  Clock3,
  Heart,
  MessageCircle,
  Search,
  Share2,
  Sparkles,
  Users,
} from "lucide-react";
import BannerImg from "../image/backgroundbanner.png";

const API_BASE_URL = "";

const THEME_DESCRIPTIONS = {
  "creativity-inspiration":
    "Ideas, rituals, and works-in-progress that keep creative momentum moving.",
  "education-creative-growth":
    "Practical learning, member lessons, and reflections that build steady growth.",
  "community-growth-outreach":
    "Member stories and collaborations that strengthen shared creative work.",
  "creative-challenges-exhibitions":
    "Pacing, planning, and balance for creative work and public-facing projects.",
};

const sortOptions = [
  { value: "recent", label: "Most recent activity" },
  { value: "popular", label: "Popularity" },
  { value: "alpha", label: "Alphabetical" },
];

function slugify(value = "") {
  return value
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function humanizeSlug(value = "") {
  return value
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatDate(value) {
  if (!value) return "Recently updated";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(value));
}

function timeAgo(value) {
  if (!value) return "Recently";
  const diff = Date.now() - new Date(value).getTime();
  const minutes = Math.max(1, Math.floor(diff / 60000));
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
  return formatDate(value);
}

function stripHtml(value = "") {
  return value.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

function authorName(author) {
  if (!author) return "LWBL Member";
  const fullName = [author.firstName, author.lastName].filter(Boolean).join(" ");
  return author.preferredName || fullName || author.memberId || "LWBL Member";
}

function ThemeBlogs() {
  const { themeSlug } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [theme, setTheme] = useState({
    name: state?.themeName || humanizeSlug(themeSlug),
    slug: themeSlug,
  });
  const [blogs, setBlogs] = useState([]);
  const [commentCounts, setCommentCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sortBy, setSortBy] = useState("recent");
  const [tagFilter, setTagFilter] = useState("all");
  const [recentOnly, setRecentOnly] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let isMounted = true;

    async function fetchThemeBlogs() {
      try {
        setLoading(true);
        setError("");

        let resolvedTheme = {
          name: state?.themeName || humanizeSlug(themeSlug),
          slug: themeSlug,
        };

        const genreResponse = await fetch(`/api/genre/fetch/${themeSlug}`);
        if (genreResponse.ok) {
          const genreResult = await genreResponse.json();
          if (genreResult.success && genreResult.data) {
            resolvedTheme = {
              id: genreResult.data._id,
              name: genreResult.data.name,
              slug: genreResult.data.slug || themeSlug,
            };
          }
        }

        const blogResponse = await fetch(
          `/api/blog/fetch/genre/${encodeURIComponent(resolvedTheme.name)}?limit=60`
        );
        let blogResult = await blogResponse.json();

        if (!blogResult.success && resolvedTheme.id) {
          const fallbackResponse = await fetch(`/api/blog/fetch?genre=${resolvedTheme.id}&status=published&limit=60`);
          blogResult = await fallbackResponse.json();
        }

        if (!blogResult.success) {
          throw new Error(blogResult.message || "Theme blogs could not be loaded.");
        }

        if (!isMounted) return;
        const activeBlogs = (blogResult.blogs || []).filter((blog) => blog.status !== "archived");
        setTheme({
          id: blogResult.genre?.id || resolvedTheme.id,
          name: blogResult.genre?.name || resolvedTheme.name,
          slug: resolvedTheme.slug || slugify(blogResult.genre?.name || resolvedTheme.name),
        });
        setBlogs(activeBlogs);

        const counts = {};
        await Promise.all(
          activeBlogs.slice(0, 24).map(async (blog) => {
            try {
              const response = await fetch(`/api/comments/fetch/blog/${blog._id}`);
              const result = await response.json();
              counts[blog._id] = Number.isFinite(result.total)
                ? result.total
                : Array.isArray(result.comments)
                  ? result.comments.length
                  : 0;
            } catch {
              counts[blog._id] = 0;
            }
          })
        );
        if (isMounted) setCommentCounts(counts);
      } catch (err) {
        if (isMounted) setError(err.message || "Unable to load this theme.");
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchThemeBlogs();
    return () => {
      isMounted = false;
    };
  }, [themeSlug, state?.themeName]);

  const tags = useMemo(() => {
    const names = new Set();
    blogs.forEach((blog) => blog.genres?.forEach((genre) => genre?.name && names.add(genre.name)));
    return Array.from(names);
  }, [blogs]);

  const filteredBlogs = useMemo(() => {
    const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const normalizedQuery = query.trim().toLowerCase();

    return blogs
      .filter((blog) => {
        if (recentOnly && new Date(blog.updatedAt || blog.createdAt).getTime() < oneWeekAgo) return false;
        if (tagFilter !== "all" && !blog.genres?.some((genre) => genre.name === tagFilter)) return false;
        if (!normalizedQuery) return true;
        return `${blog.title} ${blog.description} ${authorName(blog.author)}`.toLowerCase().includes(normalizedQuery);
      })
      .sort((a, b) => {
        if (sortBy === "alpha") return a.title.localeCompare(b.title);
        if (sortBy === "popular") {
          const aScore = (commentCounts[a._id] || 0) + (a.likesCount || 0) + (a.viewsCount || 0);
          const bScore = (commentCounts[b._id] || 0) + (b.likesCount || 0) + (b.viewsCount || 0);
          return bScore - aScore;
        }
        return new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt);
      });
  }, [blogs, commentCounts, query, recentOnly, sortBy, tagFilter]);

  const featuredBlogs = filteredBlogs.slice(0, 3);
  const newThisWeek = blogs.filter(
    (blog) => Date.now() - new Date(blog.updatedAt || blog.createdAt).getTime() <= 7 * 24 * 60 * 60 * 1000
  ).length;
  const memberCount = new Set(blogs.map((blog) => blog.author?._id || blog.author?.memberId).filter(Boolean)).size;
  const themeDescription =
    THEME_DESCRIPTIONS[theme.slug] ||
    `Recent member blogs, discussions, and learning notes collected under ${theme.name}.`;

  const openBlog = (blog) => {
    navigate(`/blogs/${blog.slug}`, {
      state: {
        fromTheme: {
          name: theme.name,
          slug: theme.slug || themeSlug,
        },
      },
    });
  };

  return (
    <div className="App bg-[#f7fbfb] min-h-screen text-left">
      <Header />
      <main className="theme-hub">
        <section
          className="pt-32 pb-10 px-4 bg-[#0f1718] text-white"
          style={{
            backgroundImage: `linear-gradient(90deg, rgba(8, 15, 16, 0.9), rgba(8, 15, 16, 0.58)), url(${BannerImg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="max-w-7xl mx-auto">
            <nav className="flex flex-wrap items-center gap-2 text-sm font-outfit text-white/75 mb-8" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-[#00F0FB]">Home</Link>
              <ChevronRight size={16} />
              <Link to="/blogs" className="hover:text-[#00F0FB]">Community Blogs</Link>
              <ChevronRight size={16} />
              <span className="text-white">{theme.name}</span>
            </nav>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-8 items-end">
              <div>
                <button
                  type="button"
                  onClick={() => navigate("/blogs")}
                  className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/25 px-4 py-2 text-sm font-outfit text-white hover:border-[#00F0FB] hover:text-[#00F0FB]"
                >
                  <ArrowLeft size={16} />
                  Back to Blogs
                </button>
                <p className="font-outfit text-xs uppercase tracking-[0.25em] text-[#00F0FB] mb-3">Category Hub</p>
                <h1 className="font-cinzel text-4xl md:text-6xl leading-tight">{theme.name}</h1>
                <p className="font-outfit text-base md:text-lg text-white/82 max-w-3xl mt-5 leading-relaxed">{themeDescription}</p>
                <div className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl">
                  <div className="rounded-lg border border-white/15 bg-white/10 p-4">
                    <p className="font-cinzel text-2xl">{blogs.length}</p>
                    <p className="font-outfit text-sm text-white/75">Active Blogs</p>
                  </div>
                  <div className="rounded-lg border border-white/15 bg-white/10 p-4">
                    <p className="font-cinzel text-2xl">{newThisWeek}</p>
                    <p className="font-outfit text-sm text-white/75">New This Week</p>
                  </div>
                  <div className="rounded-lg border border-white/15 bg-white/10 p-4">
                    <p className="font-cinzel text-2xl">{memberCount}</p>
                    <p className="font-outfit text-sm text-white/75">Members Engaged</p>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-white/15 bg-white/10 p-3 backdrop-blur">
                <div className="grid grid-cols-3 gap-2 min-h-[280px]">
                  {featuredBlogs.map((blog, index) => (
                    <button
                      key={blog._id}
                      type="button"
                      onClick={() => openBlog(blog)}
                      className={`relative overflow-hidden rounded-lg text-left focus:outline-none focus:ring-2 focus:ring-[#00F0FB] ${
                        index === 0 ? "col-span-3 h-44" : "h-32"
                      }`}
                      aria-label={`Open featured blog ${blog.title}`}
                    >
                      <img src={`${API_BASE_URL}${blog.coverImage || BannerImg}`} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
                      <span className="absolute inset-0 bg-gradient-to-t from-black/85 to-black/10" />
                      <span className="absolute left-4 right-4 bottom-4 font-outfit text-sm font-semibold text-white leading-snug">
                        {blog.title}
                      </span>
                    </button>
                  ))}
                  {!loading && featuredBlogs.length === 0 && (
                    <div className="col-span-3 flex min-h-[280px] items-center justify-center rounded-lg bg-white/10 text-center font-outfit text-white/75">
                      No active blog highlights yet.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 py-10">
          <div className="mb-7 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4 items-end">
            <div>
              <p className="font-outfit text-sm text-[#5f6c6d]">Browse active conversations in this category.</p>
              <h2 className="font-cinzel text-3xl text-[#101617] mt-1">Active Blogs</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[220px_220px_260px] gap-3">
              <label className="relative">
                <span className="sr-only">Search blogs</span>
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#667273]" size={18} />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  className="w-full rounded-lg border border-[#d6dfdf] bg-white py-3 pl-10 pr-3 font-outfit text-sm outline-none focus:border-[#00C3FF]"
                placeholder="Search blogs"
                />
              </label>
              <select
                value={sortBy}
                onChange={(event) => setSortBy(event.target.value)}
                className="rounded-lg border border-[#d6dfdf] bg-white px-3 py-3 font-outfit text-sm outline-none focus:border-[#00C3FF]"
                aria-label="Sort blogs"
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              <select
                value={tagFilter}
                onChange={(event) => setTagFilter(event.target.value)}
                className="rounded-lg border border-[#d6dfdf] bg-white px-3 py-3 font-outfit text-sm outline-none focus:border-[#00C3FF]"
                aria-label="Filter by sub-tag"
              >
                <option value="all">All sub-tags</option>
                {tags.map((tag) => (
                  <option key={tag} value={tag}>{tag}</option>
                ))}
              </select>
            </div>
          </div>

          <label className="mb-6 inline-flex items-center gap-3 rounded-lg border border-[#d6dfdf] bg-white px-4 py-3 font-outfit text-sm text-[#2c3637]">
            <input
              type="checkbox"
              checked={recentOnly}
              onChange={(event) => setRecentOnly(event.target.checked)}
              className="h-4 w-4 accent-[#00C3FF]"
            />
            Show only blogs with new posts this week
          </label>

          {loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="h-96 rounded-lg bg-white shadow-sm animate-pulse" />
              ))}
            </div>
          )}

          {!loading && error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-6 font-outfit text-red-700">{error}</div>
          )}

          {!loading && !error && filteredBlogs.length === 0 && (
            <div className="rounded-lg border border-dashed border-[#b9c8c9] bg-white p-10 text-center">
              <Sparkles className="mx-auto text-[#00A6C8]" size={34} />
              <h3 className="font-cinzel text-2xl text-[#111] mt-4">No blogs yet</h3>
              <p className="font-outfit text-[#5f6c6d] mt-2">No active blogs match this view. Be the first to start one in this category.</p>
              <Link to="/community" className="mt-5 inline-flex rounded-full bg-[#00F0FB] px-6 py-3 font-outfit font-bold text-black">
                Join the discussion
              </Link>
            </div>
          )}

          {!loading && !error && filteredBlogs.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {filteredBlogs.map((blog) => {
                const comments = commentCounts[blog._id] || 0;
                const excerpt = stripHtml(blog.description || blog.content || "").slice(0, 180);
                return (
                  <article
                    key={blog._id}
                    className="group flex h-full flex-col overflow-hidden rounded-lg border border-[#dce6e6] bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl focus-within:ring-2 focus-within:ring-[#00C3FF]"
                  >
                    <button
                      type="button"
                      onClick={() => openBlog(blog)}
                      className="relative h-52 w-full overflow-hidden text-left"
                      aria-label={`View full blog ${blog.title}`}
                    >
                      <img src={`${API_BASE_URL}${blog.coverImage || BannerImg}`} alt="" className="h-full w-full object-cover transition duration-500 group-hover:scale-105" loading="lazy" />
                      <span className="absolute left-3 top-3 rounded-full bg-[#00F0FB] px-3 py-1 font-outfit text-xs font-bold text-black">
                        {blog.genres?.[0]?.name || theme.name}
                      </span>
                    </button>
                    <div className="flex flex-1 flex-col p-5">
                      <div className="mb-3 flex items-center justify-between gap-3 text-xs font-outfit text-[#667273]">
                        <span className="inline-flex items-center gap-1"><Clock3 size={14} /> {timeAgo(blog.updatedAt || blog.createdAt)}</span>
                        <span className="inline-flex items-center gap-1"><CalendarDays size={14} /> {formatDate(blog.createdAt)}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => openBlog(blog)}
                        className="text-left focus:outline-none"
                      >
                        <h3 className="font-cinzel text-xl leading-tight text-[#101617] group-hover:text-[#007f99]">{blog.title}</h3>
                      </button>
                      <p className="font-outfit text-sm text-[#5f6c6d] mt-3">
                        By Member - {blog.author?.memberId || "LWBL"} | {blog.author?.occupation || authorName(blog.author)}
                      </p>
                      <p className="font-outfit text-sm leading-relaxed text-[#374344] mt-4 flex-1">{excerpt}{excerpt.length >= 180 ? "..." : ""}</p>
                      <div className="mt-5 grid grid-cols-3 gap-2 text-xs font-outfit text-[#465354]">
                        <span className="inline-flex items-center gap-1 rounded-md bg-[#eef8f8] px-2 py-2"><MessageCircle size={14} /> {comments} comments</span>
                        <span className="inline-flex items-center gap-1 rounded-md bg-[#f6f3ee] px-2 py-2"><Heart size={14} /> {blog.likesCount || 0} likes</span>
                        <span className="inline-flex items-center gap-1 rounded-md bg-[#f2f5fa] px-2 py-2"><Share2 size={14} /> {blog.sharesCount || 0} shares</span>
                      </div>
                      <div className="mt-5 flex flex-wrap gap-2">
                        <button className="rounded-full border border-[#c7d4d5] px-4 py-2 font-outfit text-sm font-semibold text-[#2c3637] hover:border-[#00C3FF]" type="button">
                          Subscribe
                        </button>
                        <button className="rounded-full border border-[#c7d4d5] px-4 py-2 font-outfit text-sm font-semibold text-[#2c3637] hover:border-[#00C3FF]" type="button" onClick={() => openBlog(blog)}>
                          Comment
                        </button>
                        <button className="rounded-full bg-[#00F0FB] px-4 py-2 font-outfit text-sm font-bold text-black" type="button" onClick={() => openBlog(blog)}>
                          View Full Blog
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>

        <section className="border-t border-[#dbe6e6] bg-white px-4 py-10">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-6">
            <div>
              <h2 className="font-cinzel text-2xl text-[#101617]">Join the discussion</h2>
              <p className="font-outfit text-[#5f6c6d] mt-2">Submit your blog to this category or connect with members already building here.</p>
              <Link to="/community" className="mt-5 inline-flex items-center gap-2 rounded-full bg-black px-6 py-3 font-outfit font-bold text-white">
                <Users size={18} />
                Visit Community
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {["Creative challenges", "Member workshops", "Learning resources"].map((item) => (
                <Link key={item} to="/refine" className="rounded-lg border border-[#dce6e6] p-4 font-outfit font-semibold text-[#1d292a] hover:border-[#00C3FF]">
                  {item}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default ThemeBlogs;
